import * as THREE from 'three/webgpu';
import {AxisMode, ReferenceDiagnosticSeverity, ReferenceEntitySemanticType,} from '../ReferenceFeatureTypes';
import type {ReferenceAxisController} from './ReferenceAxisController';

/**
 * 格式化基准轴调试向量。
 *
 * @param value 向量值。
 * @returns 便于控制台查看的普通对象。
 */
function formatAxisDebugVector(value: THREE.Vector3 | undefined | null): { x: number; y: number; z: number } | null {
  if (!value) {
    return null;
  }
  return {
    x: value.x,
    y: value.y,
    z: value.z,
  };
}

/**
 * 输出基准轴双面建轴调试信息。
 *
 * @param message 调试消息。
 * @param payload 调试载荷。
 */
function logReferenceAxisSolveDebug(message: string, payload: Record<string, unknown>): void {
  console.log(`[基准轴双面求解调试] ${message}`, payload);
}

/**
 * 基准轴本地实体引用。
 */
export interface AxisEntityReference {
  /** 实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType;
  /** 槽位标识。 */
  slot: string;
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;

    /**
   * 选择层解析出的拓扑命中快照。
   *
   * 设计说明：
   * 1. 特征 replay 后旧代理对象 uuid 会失效，但该字段保存了结果体 ID 与拓扑索引；
   * 2. `resolveReference` 在 uuid 查找失败时以此为依据，从新结果体上重新获取代理对象；
   * 3. 仅基于特征几何（面/线/点）创建的基准轴会写入此字段。
   */
  topologyPick?: ResolvedTopologyPick | null;

}

/**
 * 基准轴特征定义。
 */
export interface AxisFeatureDefinition {
  /** 特征 ID。 */
  id: string;
  /** 特征类型。 */
  type: 'AxisFeature';
  /** 建轴模式。 */
  mode: AxisMode;
  /** 参考实体列表。 */
  references: AxisEntityReference[];
  /** 定义版本。 */
  version: number;
}

/**
 * 基准轴有效性枚举。
 */
export enum AxisValidity {
  /** 有效。 */
  Ok = 'ok',
  /** 无效。 */
  Error = 'error',
}

/**
 * 基准轴显示线段。
 */
export interface AxisDisplaySegment {
  /** 起点。 */
  start: THREE.Vector3;
  /** 终点。 */
  end: THREE.Vector3;
}

/**
 * 基准轴诊断项。
 */
export interface AxisDiagnostic {
  /** 诊断码。 */
  code: string;
  /** 诊断消息。 */
  message: string;
  /** 严重级别。 */
  severity: ReferenceDiagnosticSeverity;
}

/**
 * 基准轴求解后的几何结果。
 */
export interface AxisResolvedGeometry {
  /** 轴原点。 */
  origin: THREE.Vector3;
  /** 轴方向。 */
  direction: THREE.Vector3;
  /** 渲染显示线段。 */
  displaySegment: AxisDisplaySegment;
  /** 有效性。 */
  validity: AxisValidity;
  /** 可选诊断列表。 */
  diagnostics?: AxisDiagnostic[];
}

/**
 * 基准轴求解错误。
 */
export interface AxisSolveError {
  /** 错误码。 */
  code: string;
  /** 错误消息。 */
  message: string;
}

/**
 * 基准轴求解结果联合类型。
 */
export type AxisSolveResult =
  | {
    ok: true;
    geometry: AxisResolvedGeometry;
  }
  | {
    ok: false;
    error: AxisSolveError;
  };

/**
 * 基准轴容差策略。
 */
export interface AxisTolerancePolicy {
  /** 点重合阈值。 */
  pointCoincidence: number;
  /** 方向退化阈值。 */
  directionDegenerate: number;
  /** 平面平行余弦阈值。 */
  planeParallelCosEpsilon: number;
  /** 点在曲面上阈值。 */
  pointOnSurface: number;
  /** 法向稳定阈值。 */
  stableNormal: number;
}

/**
 * 默认基准轴容差策略。
 */
export const DEFAULT_AXIS_TOLERANCE: AxisTolerancePolicy = {
  pointCoincidence: 1e-6,
  directionDegenerate: 1e-9,
  planeParallelCosEpsilon: 1e-6,
  pointOnSurface: 1e-5,
  stableNormal: 1e-8,
};


/**
 * 基准轴显示长度扩展倍率。
 *
 * 设计说明：
 * 1. 基准轴的 CAD 语义是无限参考轴，显示线段只服务视口识别；
 * 2. 与线/边/轴模式保持一致，两面求交得到的有限显示段也要略微外扩；
 * 3. 统一倍率避免不同建轴模式出现可视长度风格漂移。
 */
export const REFERENCE_AXIS_DISPLAY_LENGTH_SCALE = 1.08;


/**
 * 已解析的基准轴实体。
 */
export interface AxisResolvedEntity {
  /** 实体 ID。 */
  id: string;
  /** 实体类型。 */
  type: ReferenceEntitySemanticType;
  /** 实体几何数据。 */
  data: {
    /** 点。 */
    point?: THREE.Vector3;
    /** 起点。 */
    startPoint?: THREE.Vector3;
    /** 终点。 */
    endPoint?: THREE.Vector3;
    /** 原点。 */
    origin?: THREE.Vector3;
    /** 方向。 */
    direction?: THREE.Vector3;
    /** 平面法向。 */
    planeNormal?: THREE.Vector3;
    /** 平面代表点。 */
    planePoint?: THREE.Vector3;
    /** 曲面类型（任意曲面对象均可记录）。 */
    surfaceType?: TopologySurfaceKind;
    /** 轴线原点。 */
    axisOrigin?: THREE.Vector3;
    /** 轴线方向。 */
    axisDirection?: THREE.Vector3;
    /** 轴线显示长度。 */
    axisDisplayLength?: number;
    /** 圆锥顶点。 */
    apex?: THREE.Vector3;
    /** 包围盒最小点。 */
    bboxMin?: THREE.Vector3;
    /** 包围盒最大点。 */
    bboxMax?: THREE.Vector3;
    /** 外环顶点。 */
    faceOuterLoopVertices?: THREE.Vector3[];
    /** 内环顶点集合。 */
    faceInnerLoopVertices?: THREE.Vector3[][];
  };
}

/**
 * 基准轴运行时上下文。
 */
export interface AxisFeatureRuntimeContext {
  /** 当前引用。 */
  references: AxisEntityReference[];
  /** 容差策略。 */
  tolerance: AxisTolerancePolicy;
}

/**
 * 基准轴特征容差包装。
 */
export interface AxisFeatureTolerance {
  /** 容差策略。 */
  policy: AxisTolerancePolicy;
}

/**
 * 基准轴错误消息映射表。
 */
export const AXIS_ERROR_MESSAGES: Record<string, string> = {
  NEED_LINEAR_ENTITY: '需要线性边、直线或轴',
  NEED_TWO_INTERSECTING_PLANES: '需要两个相交平面',
  COINCIDENT_POINTS: '两个点重合，无法定义轴',
  NEED_CYLINDER_OR_CONE_FACE: '请选择曲面对象',
  INVALID_POINT_FACE_COMBINATION: '点和面组合无效',
  POINT_NOT_ON_SURFACE: '点不在曲面上',
  UNSTABLE_NORMAL: '法向无法稳定求解',
  REFERENCE_LOST: '参考丢失，特征无法重建',
  REBUILD_FAILED: '重建失败',
};

/**
 * 构造失败结果。
 *
 * @param code 错误码。
 * @param message 自定义错误消息。
 * @returns 失败结果。
 */
export function fail(code: keyof typeof AXIS_ERROR_MESSAGES, message?: string): AxisSolveResult {
  return {
    ok: false,
    error: {
      code,
      message: message ?? AXIS_ERROR_MESSAGES[code],
    },
  };
}

/**
 * 构造成功结果。
 *
 * @param origin 轴原点。
 * @param direction 轴方向。
 * @param displayLength 显示长度。
 * @returns 成功结果。
 */
export function ok(origin: THREE.Vector3, direction: THREE.Vector3, displayLength: number): AxisSolveResult {
  const normalizedDirection = direction.length() < 1e-16
    ? new THREE.Vector3(1, 0, 0)
    : direction.clone().normalize();
  const halfLength = displayLength * 0.5;
  const start = origin.clone().addScaledVector(normalizedDirection, -halfLength);
  const end = origin.clone().addScaledVector(normalizedDirection, halfLength);
  return {
    ok: true,
    geometry: {
      origin: origin.clone(),
      direction: normalizedDirection,
      displaySegment: { start, end },
      validity: AxisValidity.Ok,
    },
  };
}

/**
 * 解析全部几何引用。
 *
 * @param context 求解上下文。
 * @param host 求解宿主。
 * @returns 成功时返回实体集合，失败时返回失败结果。
 */
export function resolveEntities(
  context: AxisFeatureRuntimeContext,
  host: ReferenceAxisController,
): AxisSolveResult | AxisResolvedEntity[] {
  const resolved: AxisResolvedEntity[] = [];
  for (const reference of context.references) {
    const entity = host.resolveReference(reference);
    if (!entity) {
      return fail('REFERENCE_LOST');
    }
    resolved.push(entity);
  }
  return resolved;
}

/**
 * 选取显示长度。
 *
 * @param resolved 已解析实体。
 * @returns 显示长度。
 */
export function pickDisplayLength(
  resolved: AxisResolvedEntity[],
): number {
  const lengths: number[] = [];
  for (const entity of resolved) {
    const start = entity.data.startPoint;
    const end = entity.data.endPoint;
    if (start && end) {
      lengths.push(start.distanceTo(end));
    }
    const min = entity.data.bboxMin;
    const max = entity.data.bboxMax;
    if (min && max) {
      lengths.push(min.distanceTo(max));
    }
    const outerLoopVertices = entity.data.faceOuterLoopVertices;
    if (outerLoopVertices && outerLoopVertices.length >= 2) {
      const bounds = new THREE.Box3().setFromPoints(outerLoopVertices);
      if (!bounds.isEmpty()) {
        lengths.push(bounds.min.distanceTo(bounds.max));
      }
    }
  }

  const maxLength = lengths.filter((value) => Number.isFinite(value) && value > 0).sort((left, right) => right - left)[0];
  if (!maxLength) {
    return 0;
  }
  return maxLength;
}

/**
 * 基准轴统一求解器。
 *
 * 设计说明：
 * 1. 将 `features` 目录下的定义、结果、容差、类型和工具函数统一内聚到本文件；
 * 2. 控制器在构造阶段持有该求解器实例，避免继续暴露函数式求解入口；
 * 3. 各模式的公共几何辅助算法作为类私有方法统一维护，减少跨文件跳转成本。
 */
export class ReferenceAxisSolver {
  private readonly host: ReferenceAxisController;

  /**
   * 创建基准轴统一求解器。
   *
   * @param host 求解宿主。
   */
  public constructor(host: ReferenceAxisController) {
    this.host = host;
  }

  /** 线性实体允许类型集合。 */
  private static readonly LINEAR_ACCEPTED = new Set<ReferenceEntitySemanticType>([
    ReferenceEntitySemanticType.FeatureLine,
    ReferenceEntitySemanticType.DatumAxis,
  ]);

  /** 两平面模式允许类型集合。 */
  private static readonly TWO_PLANES_ACCEPTED = new Set<ReferenceEntitySemanticType>([
    ReferenceEntitySemanticType.DatumPlane,
    ReferenceEntitySemanticType.FeatureFace,
  ]);

  /** 两点模式允许类型集合。 */
  private static readonly TWO_POINTS_ACCEPTED = new Set<ReferenceEntitySemanticType>([
    ReferenceEntitySemanticType.FeaturePoint,
    ReferenceEntitySemanticType.DatumPoint,
  ]);

  /** 圆柱/圆锥面模式允许类型集合。 */
  private static readonly CYL_OR_CONE_ACCEPTED = new Set<ReferenceEntitySemanticType>([
    ReferenceEntitySemanticType.FeatureSurface,
  ]);

  /** 点面组合中的点类允许类型集合。 */
  private static readonly POINT_AND_FACE_POINT_ACCEPTED = new Set<ReferenceEntitySemanticType>([
    ReferenceEntitySemanticType.FeaturePoint,
    ReferenceEntitySemanticType.DatumPoint,
  ]);

  /** 点面组合中的面类允许类型集合。 */
  private static readonly POINT_AND_FACE_FACE_ACCEPTED = new Set<ReferenceEntitySemanticType>([
    ReferenceEntitySemanticType.DatumPlane,
    ReferenceEntitySemanticType.FeatureFace,
    ReferenceEntitySemanticType.FeatureSurface,
  ]);

  /**
   * 求解基准轴定义。
   *
   * @param definition 基准轴定义。
   * @returns 求解结果。
   */
  public solve(definition: AxisFeatureDefinition): AxisSolveResult {
    const runtimeContext: AxisFeatureRuntimeContext = {
      references: definition.references,
      tolerance: this.host.getAxisTolerance(),
    };

    const resolved = resolveEntities(runtimeContext, this.host);
    if (!Array.isArray(resolved)) {
      return resolved;
    }

    switch (definition.mode) {
      case AxisMode.LineEdgeAxis:
        return this.solveFromLinearEntity(resolved, runtimeContext);
      case AxisMode.TwoPlanes:
        return this.solveFromTwoPlanarRefs(resolved, runtimeContext);
      case AxisMode.TwoPoints:
        return this.solveFromTwoPointRefs(resolved, runtimeContext);
      case AxisMode.CylOrConeFace:
        return this.solveFromCylOrConeFace(resolved, runtimeContext);
      case AxisMode.PointAndFaceOrPlane:
        return this.solveFromPointAndFaceOrPlane(resolved, runtimeContext);
      default:
        return {
          ok: false,
          error: {
            code: 'REBUILD_FAILED',
            message: '重建失败',
          },
        };
    }
  }

  /**
   * 线/边/轴模式求解。
   *
   * @param resolved 已解析引用实体。
   * @param context 求解上下文。
   * @returns 求解结果。
   */
  private solveFromLinearEntity(
    resolved: AxisResolvedEntity[],
    context: AxisFeatureRuntimeContext,
  ): AxisSolveResult {
    const target = resolved[0];
    if (!target || !ReferenceAxisSolver.LINEAR_ACCEPTED.has(target.type)) {
      return fail('NEED_LINEAR_ENTITY');
    }

    const sourceStart = target.data.startPoint ?? null;
    const sourceEnd = target.data.endPoint ?? null;
    const origin = sourceStart && sourceEnd
      ? sourceStart.clone().add(sourceEnd).multiplyScalar(0.5)
      : (target.data.origin ?? target.data.startPoint);
    const direction = target.data.direction
      ?? (target.data.startPoint && target.data.endPoint
        ? target.data.endPoint.clone().sub(target.data.startPoint)
        : undefined);

    if (!origin || !direction) {
      return fail('NEED_LINEAR_ENTITY');
    }

    const length = direction.length();
    if (length <= context.tolerance.directionDegenerate) {
      return fail('NEED_LINEAR_ENTITY');
    }


      /**
     * 线/边/轴模式显示语义：
     * 1. 正式基准轴与预览都应以所选线段中点作为原点；
     * 2. 显示长度优先跟随所选线段自身长度；
     * 3. 只允许比源线段“略长一点”，不能退回模型包围盒级别的大跨度。
     */
    const sourceSegmentLength = sourceStart && sourceEnd
      ? sourceStart.distanceTo(sourceEnd)
      : null;
    const displayLength = sourceSegmentLength && Number.isFinite(sourceSegmentLength) && sourceSegmentLength > 0
      ? sourceSegmentLength * REFERENCE_AXIS_DISPLAY_LENGTH_SCALE
      : pickDisplayLength(resolved);

      return ok(origin, direction, displayLength);
  }

  /**
   * 两平面模式求解。
   *
   * @param resolved 已解析引用实体。
   * @param context 求解上下文。
   * @returns 求解结果。
   */
  private solveFromTwoPlanarRefs(
    resolved: AxisResolvedEntity[],
    context: AxisFeatureRuntimeContext,
  ): AxisSolveResult {
    const first = resolved[0];
    const second = resolved[1];
    if (!first || !second) {
      return fail('NEED_TWO_INTERSECTING_PLANES');
    }
    if (!ReferenceAxisSolver.TWO_PLANES_ACCEPTED.has(first.type) || !ReferenceAxisSolver.TWO_PLANES_ACCEPTED.has(second.type)) {
      return fail('NEED_TWO_INTERSECTING_PLANES');
    }

    const firstNormal = first.data.planeNormal;
    const secondNormal = second.data.planeNormal;
    const firstPoint = first.data.planePoint;
    const secondPoint = second.data.planePoint;
    if (!firstNormal || !secondNormal || !firstPoint || !secondPoint) {
      return fail('NEED_TWO_INTERSECTING_PLANES');
    }

    const normalizedFirstNormal = firstNormal.clone().normalize();
    const normalizedSecondNormal = secondNormal.clone().normalize();
    const crossDirection = normalizedFirstNormal.clone().cross(normalizedSecondNormal);
    const denominator = crossDirection.dot(crossDirection);
    logReferenceAxisSolveDebug('双面建轴输入', {
      first: {
        id: first.id,
        type: first.type,
        planeNormal: formatAxisDebugVector(firstNormal),
        planePoint: formatAxisDebugVector(firstPoint),
        faceOuterLoopVertexCount: first.data.faceOuterLoopVertices?.length ?? 0,
      },
      second: {
        id: second.id,
        type: second.type,
        planeNormal: formatAxisDebugVector(secondNormal),
        planePoint: formatAxisDebugVector(secondPoint),
        faceOuterLoopVertexCount: second.data.faceOuterLoopVertices?.length ?? 0,
      },
      normalizedFirstNormal: formatAxisDebugVector(normalizedFirstNormal),
      normalizedSecondNormal: formatAxisDebugVector(normalizedSecondNormal),
      crossDirection: formatAxisDebugVector(crossDirection),
      crossDirectionLength: crossDirection.length(),
      denominator,
      tolerance: context.tolerance,
    });
    if (denominator <= context.tolerance.directionDegenerate) {
      logReferenceAxisSolveDebug('双面建轴失败：平面近似平行', {
        denominator,
        directionDegenerate: context.tolerance.directionDegenerate,
      });
      return fail('NEED_TWO_INTERSECTING_PLANES');
    }

    const firstPlaneDistance = normalizedFirstNormal.dot(firstPoint);
    const secondPlaneDistance = normalizedSecondNormal.dot(secondPoint);
    const firstTerm = normalizedSecondNormal.clone().cross(crossDirection);
    const secondTerm = crossDirection.clone().cross(normalizedFirstNormal);
    const origin = firstTerm
      .clone()
      .multiplyScalar(firstPlaneDistance)
      .add(secondTerm.clone().multiplyScalar(secondPlaneDistance))
      .multiplyScalar(1 / denominator);

    const firstBounds = first.data.bboxMin && first.data.bboxMax
      ? { min: first.data.bboxMin, max: first.data.bboxMax }
      : null;
    const secondBounds = second.data.bboxMin && second.data.bboxMax
      ? { min: second.data.bboxMin, max: second.data.bboxMax }
      : null;
    const firstFaceRange = first.data.faceOuterLoopVertices && first.data.planeNormal
      ? this.intersectLineWithPolygonRange(origin, crossDirection, first.data.faceOuterLoopVertices, first.data.planeNormal)
      : null;
    const secondFaceRange = second.data.faceOuterLoopVertices && second.data.planeNormal
      ? this.intersectLineWithPolygonRange(origin, crossDirection, second.data.faceOuterLoopVertices, second.data.planeNormal)
      : null;

    logReferenceAxisSolveDebug('双面建轴中间量', {
      firstPlaneDistance,
      secondPlaneDistance,
      origin: formatAxisDebugVector(origin),
      firstFaceRange,
      secondFaceRange,
      firstBounds: firstBounds
        ? {
          min: formatAxisDebugVector(firstBounds.min),
          max: formatAxisDebugVector(firstBounds.max),
        }
        : null,
      secondBounds: secondBounds
        ? {
          min: formatAxisDebugVector(secondBounds.min),
          max: formatAxisDebugVector(secondBounds.max),
        }
        : null,
    });

    let displayRange: [number, number] | null = null;
    if (firstFaceRange && secondFaceRange) {
      const overlapMin = Math.max(firstFaceRange[0], secondFaceRange[0]);
      const overlapMax = Math.min(firstFaceRange[1], secondFaceRange[1]);
      if (overlapMax > overlapMin) {
        displayRange = [overlapMin, overlapMax];
      }
    }

    if (!displayRange) {
      displayRange = this.tryResolveIntersectionLengthFromFaceBounds(
        origin,
        crossDirection,
        firstBounds,
        secondBounds,
      );
    }

    logReferenceAxisSolveDebug('双面建轴显示范围', {
      displayRange,
    });

    if (displayRange) {
      const directionLength = crossDirection.length();
      if (directionLength <= context.tolerance.directionDegenerate) {
        logReferenceAxisSolveDebug('双面建轴失败：显示方向退化', {
          directionLength,
          directionDegenerate: context.tolerance.directionDegenerate,
        });
        return fail('NEED_TWO_INTERSECTING_PLANES');
      }
      const middleParameter = (displayRange[0] + displayRange[1]) * 0.5;
      const displayOrigin = origin.clone().addScaledVector(crossDirection, middleParameter);

        /**
       * 两面求交得到的有限范围只代表参考面的交线可见跨度。
       * 真实基准轴仍是无限参考轴，因此显示段应像选取单条特征线一样略微超出参考范围，
       * 避免轴线端点与面边界重合时在视口中难以识别。
       */
      const displayLength = (displayRange[1] - displayRange[0])
        * directionLength
        * REFERENCE_AXIS_DISPLAY_LENGTH_SCALE;

        logReferenceAxisSolveDebug('双面建轴输出（重叠范围）', {
        displayOrigin: formatAxisDebugVector(displayOrigin),
        direction: formatAxisDebugVector(crossDirection),
        directionLength,
        displayLength,
      });
      return ok(displayOrigin, crossDirection, displayLength);
    }

    const fallbackDisplayLength = pickDisplayLength(resolved);
    logReferenceAxisSolveDebug('双面建轴输出（回退长度）', {
      origin: formatAxisDebugVector(origin),
      direction: formatAxisDebugVector(crossDirection),
      directionLength: crossDirection.length(),
      fallbackDisplayLength,
    });
    return ok(origin, crossDirection, fallbackDisplayLength);
  }

  /**
   * 两点模式求解。
   *
   * @param resolved 已解析引用实体。
   * @param context 求解上下文。
   * @returns 求解结果。
   */
  private solveFromTwoPointRefs(
    resolved: AxisResolvedEntity[],
    context: AxisFeatureRuntimeContext,
  ): AxisSolveResult {
    const first = resolved[0];
    const second = resolved[1];

    if (!first || !second) {
      return fail('COINCIDENT_POINTS');
    }
    if (!ReferenceAxisSolver.TWO_POINTS_ACCEPTED.has(first.type) || !ReferenceAxisSolver.TWO_POINTS_ACCEPTED.has(second.type)) {
      return fail('COINCIDENT_POINTS');
    }

    const firstPoint = first.data.point;
    const secondPoint = second.data.point;
    if (!firstPoint || !secondPoint) {
      return fail('COINCIDENT_POINTS');
    }

    if (firstPoint.distanceTo(secondPoint) <= context.tolerance.pointCoincidence) {
      return fail('COINCIDENT_POINTS');
    }


      /**
     * @description 两点/顶点模式下，显示段长度严格等于两点距离。
     * 说明：
     * 1. 轴方向使用 p1->p2；
     * 2. 轴原点使用两点中点；
     * 3. 显示长度不做额外放大，保持与两点连线一致。
     */
    const displayLength = firstPoint.distanceTo(secondPoint);
    return ok(
      firstPoint.clone().add(secondPoint).multiplyScalar(0.5),
      secondPoint.clone().sub(firstPoint),
      displayLength,
    );

  }

  /**
   * 圆柱/圆锥面模式求解。
   *
   * @param resolved 已解析引用实体。
   * @param context 求解上下文。
   * @returns 求解结果。
   */
  private solveFromCylOrConeFace(
    resolved: AxisResolvedEntity[],
    context: AxisFeatureRuntimeContext,
  ): AxisSolveResult {
    const face = resolved[0];
    if (!face || !ReferenceAxisSolver.CYL_OR_CONE_ACCEPTED.has(face.type)) {
      return fail('NEED_CYLINDER_OR_CONE_FACE');
    }

    const origin = face.data.axisOrigin ?? face.data.apex;
    const direction = face.data.axisDirection;
    if (!origin || !direction) {
      return fail('NEED_CYLINDER_OR_CONE_FACE');
    }

    const length = direction.length();
    if (length <= context.tolerance.directionDegenerate) {
      return fail('NEED_CYLINDER_OR_CONE_FACE');
    }


      const resolvedDisplayLength = this.resolveCylOrConeDisplayLength(face, direction, context);
    const displayLength = resolvedDisplayLength
      ?? pickDisplayLength(resolved);


      return ok(origin, direction, displayLength);
  }


    /**
   * 解析圆柱/圆锥面模式的显示长度。
   *
   * 规则：
   * 1. 若上游已提供 `axisDisplayLength`，优先使用并做轻量放大；
   * 2. 否则尝试从曲面外环顶点沿轴向投影求跨度；
   * 3. 都不可用时返回 `null`，由调用方回退到通用显示长度策略。
   *
   * @param face 圆柱/圆锥面实体。
   * @param direction 轴方向。
   * @param context 求解上下文。
   * @returns 显示长度；无法解析时返回 `null`。
   */
  private resolveCylOrConeDisplayLength(
    face: AxisResolvedEntity,
    direction: THREE.Vector3,
    context: AxisFeatureRuntimeContext,
  ): number | null {
    const axisDisplayLength = face.data.axisDisplayLength;
    if (Number.isFinite(axisDisplayLength) && Number(axisDisplayLength) > context.tolerance.directionDegenerate) {
      return Number(axisDisplayLength) * 1.2;
    }

    const axisDirection = direction.clone().normalize();
    const outerLoopVertices = face.data.faceOuterLoopVertices ?? [];
    if (outerLoopVertices.length >= 2) {
      let minProjection = Number.POSITIVE_INFINITY;
      let maxProjection = Number.NEGATIVE_INFINITY;

      outerLoopVertices.forEach((vertex) => {
        const projection = vertex.dot(axisDirection);
        if (projection < minProjection) {
          minProjection = projection;
        }
        if (projection > maxProjection) {
          maxProjection = projection;
        }
      });

      const span = maxProjection - minProjection;
      if (Number.isFinite(span) && span > context.tolerance.directionDegenerate) {
        return span * 1.2;
      }
    }

    return null;
  }


    /**
   * 点和面/平面模式求解。
   *
   * @param resolved 已解析引用实体。
   * @param context 求解上下文。
   * @returns 求解结果。
   */
  private solveFromPointAndFaceOrPlane(
    resolved: AxisResolvedEntity[],
    context: AxisFeatureRuntimeContext,
  ): AxisSolveResult {
    if (resolved.length < 2) {
      return fail('INVALID_POINT_FACE_COMBINATION');
    }

    const first = resolved[0];
    const second = resolved[1];
    const pointEntity = ReferenceAxisSolver.POINT_AND_FACE_POINT_ACCEPTED.has(first.type)
      ? first
      : (ReferenceAxisSolver.POINT_AND_FACE_POINT_ACCEPTED.has(second.type) ? second : null);
    const faceEntity = ReferenceAxisSolver.POINT_AND_FACE_FACE_ACCEPTED.has(first.type)
      ? first
      : (ReferenceAxisSolver.POINT_AND_FACE_FACE_ACCEPTED.has(second.type) ? second : null);

    if (!pointEntity || !faceEntity) {
      return fail('INVALID_POINT_FACE_COMBINATION');
    }

    const point = pointEntity.data.point;
    if (!point) {
      return fail('INVALID_POINT_FACE_COMBINATION');
    }

    const surfaceType = faceEntity.data.surfaceType;
    const normal = faceEntity.data.planeNormal ?? faceEntity.data.axisDirection;
    if (!normal) {
      return fail('UNSTABLE_NORMAL');
    }

    const normalLength = normal.length();
    if (normalLength <= context.tolerance.stableNormal) {
      return fail('UNSTABLE_NORMAL');
    }

    if (surfaceType) {
      const surfacePoint = faceEntity.data.planePoint ?? faceEntity.data.origin ?? faceEntity.data.axisOrigin;
      if (!surfacePoint) {
        return fail('INVALID_POINT_FACE_COMBINATION');
      }

      const delta = point.clone().sub(surfacePoint);
      const signedDistance = Math.abs(delta.dot(normal) / normalLength);
      if (signedDistance > context.tolerance.pointOnSurface) {
        return fail('POINT_NOT_ON_SURFACE');
      }
    }

    return ok(point, normal, pickDisplayLength(resolved));
  }

  /**
   * 计算二维叉积。
   *
   * @param first 第一向量。
   * @param second 第二向量。
   * @returns 叉积结果。
   */
  private cross2D(first: [number, number], second: [number, number]): number {
    return first[0] * second[1] - first[1] * second[0];
  }

  /**
   * 为平面法向构造局部正交基。
   *
   * @param normal 平面法向。
   * @returns 局部二维基向量。
   */
  private buildPlaneBasis(normal: THREE.Vector3): { u: THREE.Vector3; v: THREE.Vector3 } {
    const seed = Math.abs(normal.x) < 0.9
      ? new THREE.Vector3(1, 0, 0)
      : new THREE.Vector3(0, 1, 0);
    const u = normal.clone().cross(seed).normalize();
    const v = normal.clone().cross(u).normalize();
    return { u, v };
  }

  /**
   * 将三维点投影到平面二维坐标。
   *
   * @param point 待投影点。
   * @param base 平面基点。
   * @param u 第一基向量。
   * @param v 第二基向量。
   * @returns 二维坐标。
   */
  private project2D(
    point: THREE.Vector3,
    base: THREE.Vector3,
    u: THREE.Vector3,
    v: THREE.Vector3,
  ): [number, number] {
    const delta = point.clone().sub(base);
    return [delta.dot(u), delta.dot(v)];
  }

  /**
   * 计算直线与多边形边界的参数区间。
   *
   * @param lineOrigin 直线原点。
   * @param lineDirection 直线方向。
   * @param polygon 多边形顶点。
   * @param planeNormal 所属平面法向。
   * @param epsilon 容差。
   * @returns 相交参数区间；无效时返回 null。
   */
  private intersectLineWithPolygonRange(
    lineOrigin: THREE.Vector3,
    lineDirection: THREE.Vector3,
    polygon: THREE.Vector3[],
    planeNormal: THREE.Vector3,
    epsilon = 1e-9,
  ): [number, number] | null {
    if (!polygon || polygon.length < 2) {
      return null;
    }

    const base = polygon[0];
    const { u, v } = this.buildPlaneBasis(planeNormal);
    const projectedLineOrigin = this.project2D(lineOrigin, base, u, v);
    const lineSecondPoint = lineOrigin.clone().add(lineDirection);
    const projectedLineSecondPoint = this.project2D(lineSecondPoint, base, u, v);
    const direction2D: [number, number] = [
      projectedLineSecondPoint[0] - projectedLineOrigin[0],
      projectedLineSecondPoint[1] - projectedLineOrigin[1],
    ];
    const direction2DLengthSquared = direction2D[0] * direction2D[0] + direction2D[1] * direction2D[1];
    const directionLengthSquared = lineDirection.lengthSq();
    if (direction2DLengthSquared <= epsilon || directionLengthSquared <= epsilon) {
      return null;
    }

    const parameters: number[] = [];
    const count = polygon.length;
    for (let index = 0; index < count; index += 1) {
      const firstPoint3D = polygon[index];
      const secondPoint3D = polygon[(index + 1) % count];
      const firstPoint2D = this.project2D(firstPoint3D, base, u, v);
      const secondPoint2D = this.project2D(secondPoint3D, base, u, v);
      const edgeDirection: [number, number] = [
        secondPoint2D[0] - firstPoint2D[0],
        secondPoint2D[1] - firstPoint2D[1],
      ];
      const denominator = this.cross2D(direction2D, edgeDirection);
      const lineToEdgeStart: [number, number] = [
        firstPoint2D[0] - projectedLineOrigin[0],
        firstPoint2D[1] - projectedLineOrigin[1],
      ];

      if (Math.abs(denominator) <= epsilon) {
        if (Math.abs(this.cross2D(lineToEdgeStart, direction2D)) <= epsilon) {
          const firstParameter = firstPoint3D.clone().sub(lineOrigin).dot(lineDirection) / directionLengthSquared;
          const secondParameter = secondPoint3D.clone().sub(lineOrigin).dot(lineDirection) / directionLengthSquared;
          parameters.push(firstParameter, secondParameter);
        }
        continue;
      }

      const lineParameter = this.cross2D(lineToEdgeStart, edgeDirection) / denominator;
      const edgeParameter = this.cross2D(lineToEdgeStart, direction2D) / denominator;
      if (edgeParameter >= -epsilon && edgeParameter <= 1 + epsilon) {
        parameters.push(lineParameter);
      }
    }

    if (parameters.length < 2) {
      return null;
    }

    parameters.sort((left, right) => left - right);
    return [parameters[0], parameters[parameters.length - 1]];
  }

  /**
   * 计算直线与包围盒的参数裁剪区间。
   *
   * @param origin 直线原点。
   * @param direction 直线方向。
   * @param min 包围盒最小点。
   * @param max 包围盒最大点。
   * @returns 裁剪参数区间；无交时返回 null。
   */
  private clipLineToAabb(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    min: THREE.Vector3,
    max: THREE.Vector3,
  ): [number, number] | null {
    let tMin = Number.NEGATIVE_INFINITY;
    let tMax = Number.POSITIVE_INFINITY;

    for (let axis = 0; axis < 3; axis += 1) {
      const originComponent = axis === 0 ? origin.x : (axis === 1 ? origin.y : origin.z);
      const directionComponent = axis === 0 ? direction.x : (axis === 1 ? direction.y : direction.z);
      const minComponent = axis === 0 ? min.x : (axis === 1 ? min.y : min.z);
      const maxComponent = axis === 0 ? max.x : (axis === 1 ? max.y : max.z);

      if (Math.abs(directionComponent) <= 1e-12) {
        if (originComponent < minComponent || originComponent > maxComponent) {
          return null;
        }
        continue;
      }

      const parameter1 = (minComponent - originComponent) / directionComponent;
      const parameter2 = (maxComponent - originComponent) / directionComponent;
      const near = Math.min(parameter1, parameter2);
      const far = Math.max(parameter1, parameter2);
      tMin = Math.max(tMin, near);
      tMax = Math.min(tMax, far);
      if (tMin > tMax) {
        return null;
      }
    }

    if (!Number.isFinite(tMin) || !Number.isFinite(tMax)) {
      return null;
    }
    return [tMin, tMax];
  }

  /**
   * 根据两个面的包围盒估算交线可显示区间。
   *
   * @param origin 交线原点。
   * @param direction 交线方向。
   * @param firstBounds 第一面包围盒。
   * @param secondBounds 第二面包围盒。
   * @returns 显示参数区间；无法确定时返回 null。
   */
  private tryResolveIntersectionLengthFromFaceBounds(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    firstBounds: { min: THREE.Vector3; max: THREE.Vector3 } | null,
    secondBounds: { min: THREE.Vector3; max: THREE.Vector3 } | null,
  ): [number, number] | null {
    if (!firstBounds || !secondBounds) {
      return null;
    }

    const firstRange = this.clipLineToAabb(origin, direction, firstBounds.min, firstBounds.max);
    const secondRange = this.clipLineToAabb(origin, direction, secondBounds.min, secondBounds.max);
    if (!firstRange || !secondRange) {
      return null;
    }

    const overlapMin = Math.max(firstRange[0], secondRange[0]);
    const overlapMax = Math.min(firstRange[1], secondRange[1]);
    if (!(overlapMax > overlapMin)) {
      return null;
    }

    const length = overlapMax - overlapMin;
    if (!Number.isFinite(length) || length <= 0) {
      return null;
    }

    return [overlapMin, overlapMax];
  }
}
